"use client";

import dynamic from "next/dynamic";
import { useEffect, useRef, useState } from "react";

import { onFirstInteraction } from "@/lib/on-first-interaction";

/**
 * Altura que a demo ocupa quando montada, medida no navegador: 1261px a 375px
 * de viewport e 1242px a 1280px (descontado o `py` da seção). O reservatório
 * existe para o placeholder empurrar a página exatamente como a demo
 * empurraria — sem ele, montar a demo deslocaria tudo abaixo e o CLS da home,
 * hoje em 0,000, deixaria de ser zero.
 */
const RESERVA = "min-h-[1213px] md:min-h-[1178px]";

const InteractiveDemo = dynamic(
  () => import("@/components/interactive-demo").then((m) => ({ default: m.InteractiveDemo })),
  { ssr: false, loading: () => <div className={RESERVA} aria-hidden /> },
);

/**
 * Monta a demo no primeiro gesto do usuário.
 *
 * A demo arrasta o chunk de vendor mais pesado da home (1,4MB: slate, radix e
 * recharts). Os doze demos já usavam `next/dynamic`, mas o Next faz o preload
 * de todos assim que o pai monta, então era preciso adiar o pai.
 *
 * Adiar para o idle não bastou: no stage o chunk ainda rodava aos 6,5s, dentro
 * da janela que o PageSpeed mede, com ~200ms de tarefa longa. E um raio do
 * IntersectionObserver também não serve de gatilho: a seção começa só 218px
 * abaixo da dobra, dentro de qualquer margem útil. O primeiro gesto resolve os
 * dois — quem rola começa a carregar no primeiro pixel, com a seção ainda a
 * 200px de distância, e um laboratório, que não rola, nunca paga.
 *
 * O observer fica só como rede: se a seção já estiver na tela sem gesto
 * nenhum (uma âncora, uma restauração de scroll), ela monta assim mesmo.
 */
export function InteractiveDemoLazy() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;

    const show = () => setVisible(true);
    const cancelInteraction = onFirstInteraction(show);

    let observer: IntersectionObserver | null = null;
    const node = ref.current;
    if (node && typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) show();
      });
      observer.observe(node);
    }

    return () => {
      cancelInteraction();
      observer?.disconnect();
    };
  }, [visible]);

  return (
    <div ref={ref} className={visible ? undefined : RESERVA}>
      {visible ? <InteractiveDemo variant="section" /> : null}
    </div>
  );
}
